/**
 * Tab Content Translator
 * ----------------------
 * Translates dynamically loaded product content inside .tab-content
 * that Weglot does not catch automatically.
 *
 * Targets (innerText, stored in data-original-text):
 *   - .tt-title a          product titles
 *   - .tt-add-info li a    category labels
 *
 * Attribute target (stored in data-original-tooltip):
 *   - .tt-btn-quickview[data-tooltip]    quick view tooltip
 *
 * Note: button translation is handled entirely by button-translator.js
 *
 * Handles:
 *   1. Language switch  → translate or restore .tab-content
 *   2. Ajax tab load    → MutationObserver on .tab-content
 *   3. Initial page load in non-source language
 */

'use strict';

const TAB_TEXT_SELECTORS   = '.tt-title a, .tt-add-info li a';
const TAB_TOOLTIP_SELECTOR = '.tt-btn-quickview[data-tooltip]';

let tabContent  = null;
let tabSrcLang  = 'sr';

// =============================================================================
// Helpers
// =============================================================================

function tabBaseLang(lang) {
  return lang ? lang.split('-')[0].toLowerCase() : '';
}

function tabIsSource(lang) {
  return tabBaseLang(lang) === tabBaseLang(tabSrcLang);
}

function tabFix(text) {
  return text.replace(/\bCCP\b/g, 'KKP');
}

function tabWeglotTranslate(words, lang, callback) {
  if (typeof Weglot === 'undefined' || !lang || words.length === 0) return;
  Weglot.translate({ words, languageTo: lang }, callback);
}

// =============================================================================
// Text  (.tt-title, .tt-add-info)
// =============================================================================

function tabSaveTextOriginals(container) {
  container.querySelectorAll(TAB_TEXT_SELECTORS).forEach(el => {
    if (!el.hasAttribute('data-original-text')) {
      const text = el.innerText.trim();
      if (text) el.setAttribute('data-original-text', text);
    }
  });
}

function tabRestoreTextOriginals(container) {
  container.querySelectorAll(TAB_TEXT_SELECTORS).forEach(el => {
    const original = el.getAttribute('data-original-text');
    if (original) el.innerText = original;
  });
}

function tabTranslateText(container, lang) {
  const elements = Array.from(container.querySelectorAll(TAB_TEXT_SELECTORS))
    .filter(el => el.hasAttribute('data-original-text'));

  const words = elements.map(el => ({ t: 1, w: el.getAttribute('data-original-text') }));

  tabWeglotTranslate(words, lang, data => {
    elements.forEach((el, i) => {
      if (data[i]) el.innerText = tabFix(data[i]);
    });
  });
}

// =============================================================================
// Tooltips
// =============================================================================

function tabSaveTooltipOriginals(container) {
  container.querySelectorAll(TAB_TOOLTIP_SELECTOR).forEach(el => {
    if (!el.hasAttribute('data-original-tooltip')) {
      const text = el.getAttribute('data-tooltip');
      if (text) el.setAttribute('data-original-tooltip', text);
    }
  });
}

function tabRestoreTooltipOriginals(container) {
  container.querySelectorAll(TAB_TOOLTIP_SELECTOR).forEach(el => {
    const original = el.getAttribute('data-original-tooltip');
    if (original) el.setAttribute('data-tooltip', original);
  });
}

function tabTranslateTooltips(container, lang) {
  const elements = Array.from(container.querySelectorAll(TAB_TOOLTIP_SELECTOR))
    .filter(el => el.hasAttribute('data-original-tooltip'));

  const words = elements.map(el => ({ t: 1, w: el.getAttribute('data-original-tooltip') }));

  tabWeglotTranslate(words, lang, data => {
    elements.forEach((el, i) => {
      if (data[i]) el.setAttribute('data-tooltip', tabFix(data[i]));
    });
  });
}

// =============================================================================
// Combined
// =============================================================================

function tabSaveAll(container) {
  tabSaveTextOriginals(container);
  tabSaveTooltipOriginals(container);
}

function tabRestoreAll(container) {
  tabRestoreTextOriginals(container);
  tabRestoreTooltipOriginals(container);
}

function tabTranslateAll(container, lang) {
  tabTranslateText(container, lang);
  tabTranslateTooltips(container, lang);
}

// =============================================================================
// Language change
// =============================================================================

function tabOnLangChange(newLang) {
  if (!tabContent) return;
  tabIsSource(newLang) ? tabRestoreAll(tabContent) : tabTranslateAll(tabContent, newLang);
}

// =============================================================================
// MutationObserver — ajax tab load
// =============================================================================

function tabStartObserver() {
  new MutationObserver(mutations => {
    const lang = Weglot.getCurrentLang();

    mutations.forEach(({ addedNodes }) => {
      addedNodes.forEach(node => {
        if (node.nodeType !== 1) return;
        tabSaveAll(node);
        if (!tabIsSource(lang)) tabTranslateAll(node, lang);
      });
    });
  }).observe(tabContent, { childList: true, subtree: true });
}

// =============================================================================
// Boot
// =============================================================================

function tabWaitForWeglot(callback) {
  if (typeof Weglot !== 'undefined' && typeof Weglot.getCurrentLang === 'function') {
    callback(); return;
  }
  document.addEventListener('weglot:initialized', function onWeglotInit() {
    document.removeEventListener('weglot:initialized', onWeglotInit);
    callback();
  });
  const poll = setInterval(() => {
    if (typeof Weglot !== 'undefined' && typeof Weglot.getCurrentLang === 'function') {
      clearInterval(poll); callback();
    }
  }, 150);
}

document.addEventListener('DOMContentLoaded', () => {
  tabContent = document.querySelector('.tab-content');
  if (!tabContent) return;

  // Save originals before Weglot runs
  tabSaveAll(tabContent);

  tabWaitForWeglot(() => {
    tabSrcLang = Weglot.options?.language_from ?? 'sr';

    Weglot.on('languageChanged', tabOnLangChange);
    tabStartObserver();

    const activeLang = Weglot.getCurrentLang();
    if (activeLang && !tabIsSource(activeLang)) tabTranslateAll(tabContent, activeLang);
  });
});